import React, { useRef } from 'react';
import { useParams } from 'react-router-dom';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { shallow } from 'zustand/shallow';
import { Play, Pause, ChevronDown, Edit, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';
import { PageHeader } from '../components/ui/bootstrap';

import { EditCustomProfileModal, PageParams } from '../components/ai-voices/ConfigureVoiceModal';
import SavedProfilesSkeleton from '../components/skeletons/pages/SavedProfilesSkeleton';
import { Pipe } from '../components/editor/Pipe';
import { usePreGenAudioPlayer } from '../zustand-store/usePreGenAudioPlayerStore'
import { useSavedProfiles } from '../http-hooks/saved-profiles/useSavedProfiles';
import { useDeleteProfile } from '../http-hooks/ai-voices/useDeleteProfile';
import { keyable, useSavedProfilesStore } from '../zustand-store/useSavedProfilesStore';
import { useAIVoicesStore } from '../zustand-store/useAIVoicesStore';
import { useLanguages } from '../http-hooks/ai-voices/useLanguages';
import { useVoiceConfigurationStore, VoiceConfigurationOptions } from '../zustand-store/useVoiceConfigurationStore';
import { ChangeVoiceModal } from '../components/ai-voices/ChangeVoiceModal';

export default function SavedProfiles() {

    return (
        <div>
            <PageHeader title="Saved Profiles" />
            <SavedProfilesList />
            <EditCustomProfileModal />
            <ChangeVoiceModal />
        </div>
    )
}

const SavedProfilesList = () => {

    const { org_id, id } = useParams<PageParams>();
    const setLanguages = useAIVoicesStore(state => state.setLanguages)
    const languages = useLanguages((data) => setLanguages(data));
    const { isLoading, isError, data } = useSavedProfiles({ org_id, id });
    
    if (isLoading || languages.isLoading) {
        return (
            <SavedProfilesSkeleton />
        )
    }

    if (isError) {
        return <></>
    }

    const profiles = data.data

    return (
        <div style={{maxWidth: 600}}>
            {
                profiles.map((profile: any, index: number) => (
                    <SavedProfileListItem key={index} profile={profile} index={index} />
                ))
            }
        </div>
    )
}

interface ISavedProfileListItemProps {
    profile: any
    index: number
}

const SavedProfileListItem = ({ profile, index }: ISavedProfileListItemProps) => {

    const { org_id, id } = useParams<PageParams>();
    const { expandedCardIndex, setExpandedProfile, setSelectedProfile, toggleEditProfile } = useSavedProfilesStore((state) => ({
        expandedCardIndex: state.expandedCardIndex, 
        setExpandedProfile: state.setExpandedProfile, 
        setSelectedProfile: state.setSelectedProfile,
        toggleEditProfile: state.toggleEditProfile,
    }), shallow);
    const setVoiceConfiguration = useVoiceConfigurationStore((state) => state.setVoiceConfiguration)
    const profileDetailsRef = useRef<HTMLDivElement>(null);
    const { mutateAsync: deleteProfile, isPending: isDeletingProfile } = useDeleteProfile();
    
    const languages = useAIVoicesStore((state) => state.languages);
    const language = languages[profile.lang_code];
    const voice = language?.voices.find(item => item.code === profile.voice && item.engine === profile.engine)

    const handleDeleteProfile = async () => {
        // Check if this is the default profile
        if (profile.profile_name === "Default") {
            toast.error("Cannot delete the default profile");
            return;
        }

        // Confirm deletion
        const confirmDelete = window.confirm(
            `Are you sure you want to delete the profile "${profile.profile_name}"? This action cannot be undone.`
        );

        if (!confirmDelete) {
            return;
        }

        try {
            await deleteProfile({
                org_id,
                integration_id: id,
                id: profile.id
            });
            toast.success(`Profile "${profile.profile_name}" deleted successfully`);
        } catch (error) {
            Sentry.captureException(error as any);
            console.error('Error deleting profile:', error);
            toast.error('Failed to delete profile. Please try again.');
        }
    };

    return (
        <div style={{gap: 24}} className='d-flex flex-row align-items-center px-1 my-3'>
            <div style={{border: '1px solid rgba(var(--bs-primary-rgb), 0.15)', borderRadius: 6, flexGrow: 1, padding: '6px 10px' }}>
                <div className='d-flex justify-content-between'>
                    <div className='d-flex' style={{ gap: 8, fontSize : 13, fontWeight : "bold" }}>
                        <PreviewIcon audioSrc={voice?.audio || ""} instanceKey={`saved-profiles:${profile.id}`} />
                        <div style={{ gap: 4 }} className='d-flex align-items-center'>
                            <span>
                                {profile.profile_name}
                            </span>
                        </div>
                    </div>
                    <OverlayTrigger
                        placement="top"
                        delay={{ show: 50, hide: 400 }}
                        overlay={
                            <Tooltip id={`expand-tooltip-${index}`}>
                                {expandedCardIndex === index ? 'Collapse' : 'Expand Details'}
                            </Tooltip>
                        }
                    >
                        <button
                            onClick={() => {
                                if (expandedCardIndex === index) {
                                    setExpandedProfile(null)
                                } else {
                                    setExpandedProfile(index)
                                }
                            }}
                            className="d-flex align-items-center justify-content-center"
                            style={{ 
                                transform: expandedCardIndex === index ? 'rotate(180deg)' : 'rotate(0deg)',
                                transition: 'transform 0.3s ease, background 0.2s ease',
                                background: 'transparent',
                                border: 'none',
                                borderRadius: 4,
                                width: 28,
                                height: 28,
                                cursor: 'pointer',
                                color: 'var(--bs-secondary-color)'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'rgba(101, 123, 144, 0.1)'
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'transparent'
                            }}
                        >
                            <ChevronDown size={16} />
                        </button>
                    </OverlayTrigger>
                </div>
                <div ref={profileDetailsRef} style={{overflow: 'hidden', transition: 'all 0.5s ease', height: expandedCardIndex === index ? profileDetailsRef.current?.scrollHeight : 0}}>
                    <ProfileDetails profile={profile} />
                </div>
            </div>
            <div className='d-flex flex-shrink-0 align-items-center'>
                <OverlayTrigger
                    placement="top"
                    delay={{ show: 50, hide: 400 }}
                    overlay={
                        <Tooltip id={`edit-tooltip-${profile.id}`}>Edit Profile</Tooltip>
                    }
                >
                    <button
                        onClick={() => {
                            setSelectedProfile(profile)
                            toggleEditProfile()
                            
                            setVoiceConfiguration(VoiceConfigurationOptions.volume, parseInt(profile.volume))
                            setVoiceConfiguration(VoiceConfigurationOptions.speed, parseInt(profile.speed))

                            if (profile.pitch) {
                                setVoiceConfiguration(VoiceConfigurationOptions.pitch, profile.pitch)
                            } else {
                                setVoiceConfiguration(VoiceConfigurationOptions.pitch, 'default')
                            }

                            if (profile.style) {
                                setVoiceConfiguration(VoiceConfigurationOptions.style, profile.style)
                            } else {
                                setVoiceConfiguration(VoiceConfigurationOptions.style, 'default')
                            }
                        }}
                        className="d-flex align-items-center justify-content-center"
                        style={{ 
                            color: 'var(--bs-secondary-color)',
                            background: 'transparent',
                            border: 'none',
                            borderRadius: 4,
                            width: 28,
                            height: 28,
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'rgba(101, 123, 144, 0.1)'
                            e.currentTarget.style.color = 'var(--bs-primary)'
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'transparent'
                            e.currentTarget.style.color = 'var(--bs-secondary-color)'
                        }}
                    >
                        <Edit size={16} />
                    </button>
                </OverlayTrigger>
                <Pipe />
                <OverlayTrigger
                    placement="top"
                    delay={{ show: 50, hide: 400 }}
                    overlay={
                        <Tooltip id={`delete-tooltip-${profile.id}`}>
                            {profile.profile_name === "Default" ? "Cannot delete default profile" : "Delete Profile"}
                        </Tooltip>
                    }
                >
                    <button
                        onClick={handleDeleteProfile}
                        disabled={isDeletingProfile || profile.profile_name === "Default"}
                        className="d-flex align-items-center justify-content-center"
                        style={{ 
                            color: profile.profile_name === "Default" ? 'var(--bs-border-color)' : 'var(--bs-secondary-color)',
                            background: 'transparent',
                            border: 'none',
                            borderRadius: 4,
                            width: 28,
                            height: 28,
                            cursor: profile.profile_name === "Default" ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s ease',
                            opacity: isDeletingProfile ? 0.5 : 1
                        }}
                        onMouseEnter={(e) => {
                            if (profile.profile_name !== "Default") {
                                e.currentTarget.style.background = 'rgba(220, 38, 38, 0.1)'
                                e.currentTarget.style.color = 'var(--bs-danger)'
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (profile.profile_name !== "Default") {
                                e.currentTarget.style.background = 'transparent'
                                e.currentTarget.style.color = 'var(--bs-secondary-color)'
                            }
                        }}
                    >
                        <Trash2 size={16} />
                    </button>
                </OverlayTrigger>
            </div>
        </div>
    )
}

interface IProfileDetailsProps {
    profile: keyable
}

const ProfileDetails = ({ profile }: IProfileDetailsProps) => {

    const languages = useAIVoicesStore((state) => state.languages);
    const language = languages[profile.lang_code];
    const voice = language?.voices.find(item => item.code === profile.voice && item.engine === profile.engine)

    return (
        <div className='audio-profile-card-details row' style={{padding: 20}}>
            <div className='col-6 d-flex flex-column'>
                <label>Language</label>
                <span>{language?.name}</span>
            </div>
            <div className='col-6 d-flex flex-column'>
                <label>Voice</label>
                <span className="voice">{voice?.name}</span>
            </div>
        </div>
    )
}

const PreviewIcon = ({ audioSrc, instanceKey }: IPreviewIconProps) => {

    const { activeKey, isPlaying, loadingSrc, togglePlayPause } = usePreGenAudioPlayer();
    const active = isPlaying && activeKey === instanceKey;
    const isLoadingAudio = activeKey === instanceKey && !!loadingSrc;

    return (
        <OverlayTrigger
            placement="top"
            delay={{ show: 50, hide: 400 }}
            overlay={
                <Tooltip id={`preview-tooltip-${audioSrc}`}>
                    {isLoadingAudio ? 'Loading…' : active ? 'Pause' : 'Play Preview'}
                </Tooltip>
            }
        >
            <button
                onClick={() => !isLoadingAudio && togglePlayPause(getAudioURL(audioSrc), instanceKey)}
                disabled={isLoadingAudio}
                className="d-flex align-items-center justify-content-center"
                style={{
                    background: 'rgb(227, 227, 227)',
                    boxShadow: 'none',
                    border: 'none',
                    borderRadius: 6,
                    width: 28,
                    height: 28,
                    cursor: isLoadingAudio ? 'not-allowed' : 'pointer',
                    transition: 'background 0.2s ease'
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgb(200, 200, 200)'
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgb(227, 227, 227)'
                }}
            >
                {
                    isLoadingAudio
                        ? <Loader2 size={14} className="vi-spin" />
                        : active
                            ? <Pause size={14} />
                            : <Play size={14} />
                }
            </button>
        </OverlayTrigger>
    )
}

interface IPreviewIconProps {
    audioSrc: string
    instanceKey: string
}

const getAudioURL = (audio: string) => {
    return `${process.env.REACT_APP_AUDIO_PREVIEW_URL}/${audio}`
}
